import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { formatNumber } from '@utils/usajiliFormat';

export const USAJILIPvtToDateTab: React.FC = () => {
  const { filters, refetchKey } = useUSAJILI();
  const { data, isLoading, error } = useQuery({
    queryKey: ['usajili_pvt_todate', filters, refetchKey],
    queryFn: () => usajiliApi.pvtToDate(filters),
  });

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{(error as any)?.error?.message || 'Failed to load pivot'}</Alert>;

  const dates = data?.date_headers || [];
  const counties = data?.county_headers || [];
  const rows = data?.rows || [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        pvt todate — Voters Registered (rows = dates, columns = counties)
      </Typography>
      <Paper
        sx={{
          width: '100%',
          overflow: 'auto',
          maxHeight: '75vh',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: counties.length * 120 + 150,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: 'background.default',
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 4,
                  borderBottom: '2px solid #212121',
                  minWidth: 130,
                }}
              >
                Row Labels
              </TableCell>
              {counties.map((c, j) => {
                const isGrand = c === 'Grand Total';
                return (
                  <TableCell
                    key={j}
                    align="right"
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'background.default',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: '2px solid #212121',
                      ...(isGrand
                        ? {
                            bgcolor: 'divider',
                            borderLeft: '2px solid #212121',
                          }
                        : {}),
                      minWidth: 120,
                    }}
                  >
                    {c}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {dates.map((d, i) => {
              const isGrand = d === 'Grand Total';
              return (
                <TableRow
                  key={i}
                  sx={{
                    ...(isGrand
                      ? {
                          bgcolor: 'background.default',
                          '& td': {
                            fontWeight: 700,
                            borderTop: '2px solid #212121',
                          },
                        }
                      : {}),
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: isGrand ? 700 : 600,
                      position: 'sticky',
                      left: 0,
                      bgcolor: isGrand ? 'background.default' : 'background.paper',
                      zIndex: 2,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {d}
                  </TableCell>
                  {counties.map((c, j) => {
                    const v = rows[i]?.[j] ?? 0;
                    const isGrandCol = c === 'Grand Total';
                    return (
                      <TableCell
                        key={j}
                        align="right"
                        sx={
                          isGrandCol
                            ? {
                                bgcolor: isGrand ? 'divider' : 'background.default',
                                fontWeight: 700,
                                borderLeft: '2px solid #212121',
                              }
                            : {}
                        }
                      >
                        {formatNumber(v)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
